import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Trash2, ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useWishlistStore } from '@/stores/wishlistStore';
import { useCartStore } from '@/stores/cartStore';
import { useCurrencyStore } from '@/stores/currencyStore';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import Header from '@/components/layout/Header';
import Footer from '@/components/UI/Footer';

const Wishlist = () => {
  const navigate = useNavigate();
  const { items, isLoading, fetchWishlist, removeFromWishlist } = useWishlistStore();
  const addItem = useCartStore(state => state.addItem);
  const { formatPrice } = useCurrencyStore();
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsLoggedIn(false);
        navigate('/auth');
        return;
      }

      setIsLoggedIn(true);
      fetchWishlist();
    };

    checkAuthAndFetch();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session?.user) {
        setIsLoggedIn(false);
        navigate('/auth');
      } else {
        setIsLoggedIn(true);
        fetchWishlist();
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchWishlist, navigate]);

  const handleAddToCart = (item: any) => {
    const product = item.product_data;
    const defaultVariant = product.node.variants.edges[0]?.node;

    if (!defaultVariant) {
      toast({
        title: "Product unavailable",
        description: "This product is currently unavailable.",
        variant: "destructive",
      });
      return;
    }

    const cartItem = {
      product,
      variantId: defaultVariant.id,
      variantTitle: defaultVariant.title,
      price: defaultVariant.price,
      quantity: 1,
      selectedOptions: defaultVariant.selectedOptions || []
    };
    
    addItem(cartItem);
    
    toast({
      title: "Added to cart",
      description: `${product.node.title} has been added to your cart.`,
    });
  };

  const handleRemove = async (productId: string, title: string) => {
    try {
      await removeFromWishlist(productId);
      toast({
        title: "Removed from wishlist",
        description: `${title} has been removed from your wishlist.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove item. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!isLoggedIn) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>My Wishlist | Everhouse</title>
        <meta name="description" content="View and manage your saved favorite products" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        
        <main className="flex-1 container mx-auto px-4 py-8 mt-16 md:mt-[72px]">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <Button
                variant="ghost"
                className="mb-4"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              
              <div className="flex items-center gap-3">
                <Heart className="w-8 h-8 text-primary" />
                <div>
                  <h1 className="text-3xl font-bold">My Wishlist</h1>
                  <p className="text-muted-foreground mt-1">
                    {items.length} {items.length === 1 ? 'item' : 'items'} saved
                  </p>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {isLoading && items.length === 0 && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-muted-foreground">Loading your wishlist...</p>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && items.length === 0 && (
              <div className="text-center py-12 bg-secondary/10 rounded-lg">
                <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h2 className="text-2xl font-semibold mb-2">Your wishlist is empty</h2>
                <p className="text-muted-foreground mb-6">
                  Start adding products you love to save them for later
                </p>
                <Button onClick={() => navigate('/shop')}>
                  Browse Products
                </Button>
              </div>
            )}

            {/* Wishlist Grid */}
            {items.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {items.map((item) => {
                  const product = item.product_data.node;
                  const price = parseFloat(product.priceRange.minVariantPrice.amount);
                  const currencyCode = product.priceRange.minVariantPrice.currencyCode;
                  const imageUrl = product.images.edges[0]?.node.url;

                  return (
                    <div
                      key={item.id}
                      className="group bg-card rounded-lg overflow-hidden border hover:shadow-lg transition-shadow"
                    >
                      {/* Image */}
                      <div
                        className="relative aspect-square bg-secondary/10 cursor-pointer"
                        onClick={() => navigate(`/product/${product.handle}`)}
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={product.title}
                            className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-muted-foreground">No image</span>
                          </div>
                        )}
                        
                        {/* Remove Button */}
                        <Button
                          variant="secondary"
                          size="icon"
                          className="absolute top-2 right-2 h-8 w-8 rounded-full bg-background/90 hover:bg-background opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemove(item.product_id, product.title);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>

                      {/* Details */}
                      <div className="p-4 space-y-3">
                        <div>
                          <h3
                            className="font-medium line-clamp-2 cursor-pointer hover:text-primary transition-colors"
                            onClick={() => navigate(`/product/${product.handle}`)}
                          >
                            {product.title}
                          </h3>
                          <p className="text-lg font-semibold mt-1">
                            {formatPrice(price, currencyCode)}
                          </p>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            className="flex-1"
                            size="sm"
                            onClick={() => handleAddToCart(item)}
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Add to Cart
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/product/${product.handle}`)}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Wishlist;
